import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  Signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  INormalText,
  ERelationType,
  UserDto,
  ICountry,
  EEntityType,
  IProfilePictureFile,
  CONFIGURATION_SETTING_KEYS,
} from '@web/dtos';
import { QuillViewComponent } from 'ngx-quill';
import { IAccountInfoForm } from '../../../../entities/account-info-entity';
import { AccountInfoFactoryService } from '../../../../factory/account/account-info-factory.service';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { ToTranslatedOptionPipe } from '../../../../pipes/to-select-option/to-translated-option.pipe';
import { SalutationStore } from '../../../../services/api/salutation/salutation-store.service';
import { UserStore } from '../../../../services/api/user/user/user-store.service';
import { CorporateDesignService } from '../../../../services/corporate-design/corporate-design.service';
import { AutoCompleteComponent } from '../../../../components/data-input/form-inputs/auto-complete/auto-complete.component';
import { ButtonComponent } from '../../../../components/feedback/button/button.component';
import { DropDownComponent } from '../../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { SectionComponent } from '../../../../components/layout/section/section.component';
import { TextInputComponent } from '../../../../components/data-input/form-inputs/text-input/text-input.component';
import { DateInputComponent } from '../../../../components/data-input/form-inputs/date-input/date-input/date-input.component';
import { CountryStore } from '../../../../services/api/address-validation/county/country-store.service';
import { WebTextStore } from '../../../../services/api/text/web-text-store.service';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import { LoadingAnimationComponent } from '../../../../components/feedback/loading-animation/loading-animation.component';
import { ConfigurationStore } from '../../../../services/api/configuration/configuration-store.service';

interface IImageForm {
  imageFile: FormControl<File | null>;
  imageURL: FormControl<string | null>;
}

@Component({
  selector: 'aw-account-info-page',
  imports: [
    CommonModule,
    AutoCompleteComponent,
    ButtonComponent,
    DropDownComponent,
    FormsModule,
    ReactiveFormsModule,
    SectionComponent,
    TextInputComponent,
    FilterTextsPipe,
    LanguagePipe,
    QuillViewComponent,
    ToTranslatedOptionPipe,
    DateInputComponent,
    AppendTestIdPipe,
    LoadingAnimationComponent,
    LoadingAnimationComponent,
  ],
  templateUrl: './account-info-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountInfoPageComponent implements OnDestroy {
  protected readonly userStore: UserStore = inject(UserStore);
  protected readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  protected readonly salutationsStore: SalutationStore =
    inject(SalutationStore);
  protected readonly countryStore: CountryStore = inject(CountryStore);
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  private readonly accountInfoFactory: AccountInfoFactoryService = inject(
    AccountInfoFactoryService
  );
  protected readonly fb: FormBuilder = inject(FormBuilder);

  protected readonly salutationOptions: Signal<INormalText[]> =
    this.salutationsStore.salutationOptions;
  protected readonly loading: Signal<boolean> = this.userStore.isLoading;
  protected accountInfoForm: FormGroup<IAccountInfoForm> =
    this.accountInfoFactory.buildForm(this.userStore.user());
  protected isLoading: Signal<boolean> = this.userStore.isLoading;
  protected readonly availableCountries: Signal<ICountry[]> =
    this.countryStore.countries;
  public imageFileForm: FormGroup<IImageForm> = new FormGroup({
    imageFile: new FormControl<File | null>(null),
    imageURL: new FormControl<string | null>(null),
  });
  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly CONFIGURATION_SETTING_KEYS = CONFIGURATION_SETTING_KEYS;

  constructor() {
    effect((): void => {
      if (this.userStore.user()) {
        this.accountInfoForm = this.accountInfoFactory.buildForm(
          this.userStore.user()
        );
        if (this.accountInfoForm.value.profilePicture) {
          const profilePictureFile = this.accountInfoForm.value
            .profilePicture as unknown as IProfilePictureFile;
          this.setPreviewFromArrayBuffer(profilePictureFile.data);
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.imageFileForm.controls.imageURL.value) {
      URL.revokeObjectURL(this.imageFileForm.controls.imageURL.value);
    }
  }

  public async save(): Promise<void> {
    const file: File | null = this.imageFileForm.controls.imageFile.value;

    const userDataFromForm: UserDto = this.accountInfoFactory.buildEntity(
      this.accountInfoForm
    );

    const formData: FormData = new FormData();
    formData.append('user', JSON.stringify(userDataFromForm));

    if (file) {
      formData.append('profilePicture', file, file.name);
    }
    if (file?.arrayBuffer()) {
      this.setPreviewFromArrayBuffer(await file?.arrayBuffer());
    }
    this.userStore.updateUser(formData);
  }

  public selectFileEvent($event: Event): void {
    const files: FileList | null = ($event.target as HTMLInputElement).files;
    if (files) {
      const latestFile: number = files.length - 1;
      this.selectFile(files[latestFile]);
    }
  }

  private async selectFile(file: File): Promise<void> {
    const arrayBuffer: ArrayBuffer = await file.arrayBuffer();
    if (arrayBuffer) {
      this.setPreviewFromArrayBuffer(arrayBuffer);
      this.imageFileForm.controls.imageFile.setValue(file);
    }
  }

  private setPreviewFromArrayBuffer(arrayBuffer: ArrayBuffer): void {
    this.imageFileForm.controls.imageURL.setValue(
      this.arrayBufferToObjectURL(arrayBuffer)
    );
  }

  private arrayBufferToObjectURL(buffer: ArrayBuffer): string {
    const u8 = new Uint8Array(buffer);
    const blob = new Blob([u8]);
    return URL.createObjectURL(blob);
  }

  public deleteProfilePicture(): void {
    this.imageFileForm.controls.imageURL.setValue(null);
    this.imageFileForm.controls.imageFile.setValue(null);
    this.accountInfoForm.controls.profilePicture.setValue(null);
  }
}
